// Modèle de partition : transforme un morceau (notes en ticks, par main) en mesures
// prêtes à graver, par portée : notes et accords avec durée de notation, points, liaisons
// de prolongation quand une durée n'est pas notable d'un seul symbole, silences qui
// comblent les vides, silence de mesure entière. Ce module est pur (aucune dépendance
// au navigateur) : le graveur (VexFlow) ne fait que dessiner ce qu'il décrit.

import { HANDS, allNotes, measureCount, ticksPerBeat, ticksPerMeasure, type Hand, type Note, type Score, type TimeSignature } from './model';

/** Durées de notation, en notation VexFlow : ronde, blanche, noire, croche, double, triple. */
export type SheetDuration = 'w' | 'h' | 'q' | '8' | '16' | '32';

export interface DurationSymbol {
  vf: SheetDuration;
  dots: number;
  ticks: number;
}

export interface SheetNoteRef {
  id: string;
  pitch: number;
  finger?: number;
}

export interface SheetEvent {
  kind: 'note' | 'rest';
  /** Position absolue, en ticks. */
  tick: number;
  /** Durée notée, en ticks. */
  duration: number;
  vf: SheetDuration;
  dots: number;
  /** Notes de l'accord (vide pour un silence), triées par hauteur croissante. */
  notes: SheetNoteRef[];
  /** Liaison de prolongation vers l'événement suivant de la portée (même hauteurs). */
  tieToNext?: boolean;
  /** Silence qui couvre toute la mesure. */
  wholeMeasure?: boolean;
}

export interface SheetMeasure {
  index: number;
  tick: number;
  duration: number;
  events: SheetEvent[];
}

export interface SheetStaff {
  hand: Hand;
  clef: 'treble' | 'bass';
  measures: SheetMeasure[];
}

export interface SheetModel {
  staves: SheetStaff[];
  measureCount: number;
  ticksPerMeasure: number;
  ticksPerBeat: number;
  timeSignature: TimeSignature;
  keyFifths: number;
  ppq: number;
}

/** Valeurs notables d'un seul symbole, de la plus longue à la plus courte, pour un ppq donné. */
export function notationValues(ppq: number): DurationSymbol[] {
  const base: [SheetDuration, number][] = [
    ['w', 4],
    ['h', 2],
    ['q', 1],
    ['8', 0.5],
    ['16', 0.25],
    ['32', 0.125],
  ];
  const values: DurationSymbol[] = [];
  for (const [vf, beats] of base) {
    values.push({ vf, dots: 1, ticks: Math.round(beats * 1.5 * ppq) });
    values.push({ vf, dots: 0, ticks: Math.round(beats * ppq) });
  }
  return values.sort((a, b) => b.ticks - a.ticks);
}

/**
 * Décompose une durée en symboles de notation, du plus long au plus court.
 * 1 440 ticks → blanche pointée ; 1 200 → blanche puis croche ; 600 → noire puis double.
 */
export function decomposeDuration(ticks: number, ppq: number): DurationSymbol[] {
  const values = notationValues(ppq);
  const out: DurationSymbol[] = [];
  let remaining = ticks;
  while (remaining > 0) {
    const v = values.find((x) => x.ticks <= remaining);
    if (!v) throw new Error(`durée non notable : ${ticks} ticks`);
    out.push(v);
    remaining -= v.ticks;
  }
  return out;
}

function isStandard(ticks: number, ppq: number): boolean {
  return notationValues(ppq).some((v) => v.ticks === ticks);
}

/**
 * Découpe un vide en silences lisibles : un silence qui commence sur un temps et couvre une
 * valeur notable est écrit d'un seul symbole ; sinon on coupe au temps suivant.
 */
export function restsForGap(start: number, end: number, ppq: number, tpb: number): { tick: number; symbol: DurationSymbol }[] {
  const out: { tick: number; symbol: DurationSymbol }[] = [];
  let cursor = start;
  while (cursor < end) {
    const onBeat = cursor % tpb === 0;
    const remaining = end - cursor;
    if (onBeat && isStandard(remaining, ppq)) {
      out.push({ tick: cursor, symbol: decomposeDuration(remaining, ppq)[0] });
      cursor = end;
      continue;
    }
    const nextBeat = (Math.floor(cursor / tpb) + 1) * tpb;
    const pieceEnd = Math.min(nextBeat, end);
    for (const symbol of decomposeDuration(pieceEnd - cursor, ppq)) {
      out.push({ tick: cursor, symbol });
      cursor += symbol.ticks;
    }
  }
  return out;
}

function groupChords(notes: Note[]): { tick: number; duration: number; notes: Note[] }[] {
  const groups = new Map<number, Note[]>();
  for (const n of notes) {
    const list = groups.get(n.start);
    if (list) list.push(n);
    else groups.set(n.start, [n]);
  }
  return [...groups.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([tick, list]) => ({
      tick,
      // Dans un accord, la durée notée est celle de la note la plus courte.
      duration: Math.min(...list.map((n) => n.duration)),
      notes: list.slice().sort((a, b) => a.pitch - b.pitch),
    }));
}

/** Construit le modèle de partition pour les mains choisies. */
export function buildSheetModel(score: Score, hands: readonly Hand[] = HANDS): SheetModel {
  const ppq = score.ppq;
  const tpm = ticksPerMeasure(score);
  const tpb = ticksPerBeat(score);
  const count = measureCount(score);
  const staves: SheetStaff[] = [];
  for (const part of score.parts) {
    if (!hands.includes(part.hand)) continue;
    const chords = groupChords(allNotes(score, [part.hand]));
    const measures: SheetMeasure[] = [];
    for (let m = 0; m < count; m++) {
      const mStart = m * tpm;
      const mEnd = mStart + tpm;
      const events: SheetEvent[] = [];
      const inMeasure = chords.filter((c) => c.tick >= mStart && c.tick < mEnd);
      let cursor = mStart;
      const pushRests = (from: number, to: number) => {
        if (to <= from) return;
        if (from === mStart && to === mEnd) {
          events.push({ kind: 'rest', tick: from, duration: tpm, vf: 'w', dots: 0, notes: [], wholeMeasure: true });
          return;
        }
        for (const r of restsForGap(from, to, ppq, tpb)) {
          events.push({ kind: 'rest', tick: r.tick, duration: r.symbol.ticks, vf: r.symbol.vf, dots: r.symbol.dots, notes: [] });
        }
      };
      for (const chord of inMeasure) {
        pushRests(cursor, chord.tick);
        const end = Math.min(mEnd, chord.tick + chord.duration);
        const symbols = decomposeDuration(end - chord.tick, ppq);
        let t = chord.tick;
        symbols.forEach((symbol, i) => {
          events.push({
            kind: 'note',
            tick: t,
            duration: symbol.ticks,
            vf: symbol.vf,
            dots: symbol.dots,
            notes: chord.notes.map((n) => ({ id: n.id, pitch: n.pitch, finger: n.finger })),
            tieToNext: i < symbols.length - 1 ? true : undefined,
          });
          t += symbol.ticks;
        });
        cursor = Math.max(cursor, end);
      }
      pushRests(cursor, mEnd);
      measures.push({ index: m, tick: mStart, duration: tpm, events });
    }
    staves.push({ hand: part.hand, clef: part.clef, measures });
  }
  return { staves, measureCount: count, ticksPerMeasure: tpm, ticksPerBeat: tpb, timeSignature: score.timeSignature, keyFifths: score.keyFifths, ppq };
}

const KEY_NAMES = ['c', 'c#', 'd', 'd#', 'e', 'f', 'f#', 'g', 'g#', 'a', 'a#', 'b'];

/** Hauteur MIDI → clé VexFlow ('c/4', 'c#/4'). Les altérations sont écrites en dièses. */
export function vexKey(pitch: number): string {
  const name = KEY_NAMES[((pitch % 12) + 12) % 12];
  const octave = Math.floor(pitch / 12) - 1;
  return `${name}/${octave}`;
}

/** Durée VexFlow complète : 'q', 'qd' (pointée), 'qr' (silence). */
export function vexDuration(event: SheetEvent): string {
  return `${event.vf}${event.dots > 0 ? 'd' : ''}${event.kind === 'rest' ? 'r' : ''}`;
}
